import type { Block } from './industries';
import routes from './routes.json';

/*
 * Guide pages (/resources/<slug>): a plain-language "how to price X" article for one trade, with a sample quote and an FAQ. One file per page in
 * src/marketing/guides/<slug>.ts (default export: a GuidePage); published by listing its path in routes.json and the sitemap. Same blocks as a trade
 * page (text, list, sample-quote, faq, callout, image), so the renderer, the overlap gate and the JSON-LD checks are shared; it is always an Article in JSON-LD.
 */
export interface GuidePage {
  slug: string;
  /** `/resources/<slug>` */
  path: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  /** <= 60 characters, ends with " | Scopie" */
  title: string;
  /** <= 155 characters */
  metaDescription: string;
  h1: string;
  /** 40-60 words, first text under the H1 */
  answerFirst: string;
  /** YYYY-MM-DD: sent as datePublished */
  datePublished: string;
  /** YYYY-MM-DD: sent as dateModified, shown as "Last reviewed" */
  lastReviewed: string;
  /** Optional and always 'Article' (kept so data files that write it still type-check). */
  schemaType?: 'Article';
  blocks: Block[];
  /** Mirrors the FAQ block, so the page, its JSON-LD and the tests agree. */
  faq: { q: string; a: string }[];
  internalLinks: { href: string; anchor: string; status: 'live' | 'planned' }[];
  cta: { heading: string; text: string; button: string; href: string; fine: string };
}

export const guidePages: GuidePage[] = Object.values(import.meta.glob<{ default: GuidePage }>('./guides/*.ts', { eager: true })).map((m) => m.default);

/** The guide pages that are published (listed in routes.json). */
export const liveGuides = (): GuidePage[] => guidePages.filter((p) => routes.routes.includes(p.path));
